import { EntryDetails } from '@/api/entries/types/types';
import { EntryItemType } from '@/feature/entries-pickem/components/EntriesList';
import { checkIfBoostedProjection } from '@/feature/lobby/utils/lineupsUtils';
import {
    BoostedMultiplier,
    DynamicMultiplier,
    GameMode,
    GameType,
    MetadataType,
    Result,
    Status,
} from '@/types/api.generated';
import { PickStatus, getPickStatus } from '@/utils/pick-status';

const getDynamicMultipliers = (entryDetails: EntryItemType | EntryDetails) => {
    const voidedCount = entryDetails?.picks.filter(it => it.result === Result.Void).length ?? 0;
    // initial multipliers are those that don't take into account the voided picks
    const intialDynamicMultipliers = entryDetails?.dynamicMultipliers
        ?.filter(it => it.numberOfPicks === entryDetails?.picks.length)
        ?.sort((a, b) => a.multiplier - b.multiplier);

    // these are multipliers for the number of picks that are not voided
    const currentMultipliers = entryDetails?.dynamicMultipliers?.filter(
        it => it.numberOfPicks === entryDetails?.picks.length - voidedCount
    );

    return { intialDynamicMultipliers, currentMultipliers };
};

const getBoostedDetails = (entryDetails: EntryItemType | EntryDetails) => {
    const voidedCount = entryDetails?.picks.filter(it => it.result === Result.Void).length ?? 0;
    const boostedPicksCount =
        entryDetails?.picks.filter(it => checkIfBoostedProjection(it.projection.type)).length ?? 0;
    const voidedBoostedPicksCount =
        entryDetails?.picks.filter(it => checkIfBoostedProjection(it.projection.type) && it.result === Result.Void)
            .length ?? 0;

    const initialBoostedMultiplier = entryDetails?.boostedMultipliers?.find(
        it => it.numberOfPicks === entryDetails.picks.length && it.numberOfBoostedPicks === boostedPicksCount
    );
    const nonVoidedBoostedMultiplier = entryDetails?.boostedMultipliers?.find(
        it =>
            it.numberOfPicks === entryDetails?.picks.length - voidedCount &&
            it.numberOfBoostedPicks === boostedPicksCount - voidedBoostedPicksCount
    );

    return {
        boostedPicksCount,
        voidedBoostedPicksCount,
        initialBoostedMultiplier,
        nonVoidedBoostedMultiplier,
    };
};

const getMultipliersRange = (multipliers: DynamicMultiplier[], boostedMultiplier?: BoostedMultiplier) =>
    `${multipliers[0].multiplier}x-${boostedMultiplier?.multiplier ?? multipliers[multipliers.length - 1].multiplier}x`;

/**
 * Return the multiplier for the entry. - https://pitchtech.atlassian.net/browse/PIC-1728
 *
 * @param {EntryItemType | EntryDetails} entryDetails - Entry details containing the multipliers.
 * @param {"initial" | "adjusted" | "decimals"} multiplier - Tells is if the returned multipliers should be the initial ones or the ones adjusted based on voided picks count and based on gameType:
 * only for P2P entries can be used decimals multiplier
 */
export const getMultiplier = (entryDetails: EntryItemType | EntryDetails, multiplier: 'initial' | 'adjusted') => {
    const voidedCount = entryDetails?.picks.filter(it => it.result === Result.Void).length ?? 0;
    const { initialBoostedMultiplier, nonVoidedBoostedMultiplier } = getBoostedDetails(entryDetails);
    const { intialDynamicMultipliers, currentMultipliers } = getDynamicMultipliers(entryDetails);

    if (entryDetails?.gameMode === GameMode.Dynamic) {
        if (voidedCount > 0) {
            if (multiplier === 'adjusted') {
                if (entryDetails?.result === Result.Win) {
                    return `${entryDetails?.winningDynamicMultiplier?.multiplier}x`;
                } else {
                    // if the entry is not won, always get the multipliers for the non-voided count
                    if (currentMultipliers && currentMultipliers.length > 0) {
                        return getMultipliersRange(currentMultipliers, nonVoidedBoostedMultiplier);
                    } else {
                        // this is for cases when the entry is not settled and has voided picks
                        // and there are no multipliers for the non-voided count
                        // if there are no dynamic multipliers, we should show the perfect multiplier
                        return `${entryDetails.multiplier}x`;
                    }
                }
            } else {
                if (intialDynamicMultipliers && intialDynamicMultipliers.length > 0) {
                    return getMultipliersRange(intialDynamicMultipliers, initialBoostedMultiplier);
                }
            }
        }
        if (entryDetails?.result === Result.Win) {
            return `${entryDetails?.winningDynamicMultiplier?.multiplier}x`;
        }
        if (intialDynamicMultipliers && intialDynamicMultipliers.length > 0) {
            return getMultipliersRange(intialDynamicMultipliers, initialBoostedMultiplier);
        }
    }

    if (
        entryDetails?.gameMode === GameMode.Perfect &&
        !!intialDynamicMultipliers &&
        intialDynamicMultipliers.length > 0
    ) {
        // in this case the entry was previously dynamic and now it's a perfect entry
        // so for the adjusted multiplier we show the perfect multiplier
        // and for the initial multiplier we show the dynamic multipliers
        if (multiplier === 'adjusted') {
            return `${entryDetails.multiplier}x`;
        } else {
            return getMultipliersRange(intialDynamicMultipliers, initialBoostedMultiplier);
        }
    }
    return `${multiplier === 'adjusted' ? entryDetails?.multiplier : entryDetails?.initialMultiplier}x`;
};

export const defaultNegativeToZero = (value?: number) => (value && value > 0 ? value : 0);

export const getPayoutLabel = (entry: EntryItemType | EntryDetails) => {
    if (!entry) {
        return '';
    }
    if (getRefundedAmountFromPromo(entry)) {
        return 'Refunded';
    }
    if (entry.status === Status.CashedOut) {
        return 'Payout';
    }
    if (entry.status === Status.Pending) {
        if (isVariablePayoutEntry(entry.gameMode, entry.gameType === GameType.P2p)) {
            return 'Wins up to';
        } else {
            return 'To win';
        }
    }
    if (entry.result === Result.Void) {
        return 'Refunded';
    }
    if (entry.result === Result.Win || entry.result === Result.Loss) {
        return 'Paid';
    }
    return '';
};

export const getRefundedAmountFromPromo = (entry: EntryItemType | EntryDetails) => {
    return entry?.promotions?.[0]?.metadata?.find(data => data.type === MetadataType.RefundedAmount)?.value ?? null;
};

/**
 * Checks if the entry has a variable payout entry: if it's gameMode = Dynamic or gameType is P2P
 * we need to check this in order to display different payout labels
 */
export const isVariablePayoutEntry = (gameMode?: GameMode, p2pGameType?: boolean) => {
    return gameMode === GameMode.Dynamic || p2pGameType;
};

export const getEntryDetails = (
    entry: EntryItemType | EntryDetails,
    entryPayout: { voidedCount: number | undefined; availableCount: number | undefined }
) => {
    const p2pGameType = entry?.gameType === GameType.P2p;
    const voidedCount = entryPayout?.voidedCount;
    const multiplierType: 'initial' | 'adjusted' = p2pGameType ? (voidedCount ? 'initial' : 'adjusted') : 'initial';
    const initialMultiplierEqualsMultiplier = p2pGameType ? entry?.initialMultiplier === entry?.multiplier : false;
    const hasPicksWithStatus = (status: PickStatus) => entry?.picks.some(pick => getPickStatus(pick) === status);

    return {
        p2pGameType,
        voidedCount,
        multiplierType,
        initialMultiplierEqualsMultiplier,
        hasRevivedPicks: hasPicksWithStatus(PickStatus.REVIVED),
        hasRescuedPicks: hasPicksWithStatus(PickStatus.RESCUED),
    };
};
